/**
 * MARGIN CONVERGENCE — the rule that decides who re-meshes when a margin source arrives, plus the
 * bookkeeping that makes it work across the dispatch→apply gap.
 *
 * A chunk's mesh reads seven positive neighbours as its high-side margin (POSITIVE_MARGIN_OFFSETS_7).
 * Any source that wasn't resident at mesh time was EXTRAPOLATED from the chunk's own edge voxel, so
 * the mesh is wrong along that face/edge/corner column until it re-meshes. The arrival of a source is
 * the trigger, and `Chunk.extrapolatedMargins` is how a consumer says which sources it is waiting on.
 *
 * THE GAP THIS CLOSES. The mask is computed when the grid is expanded (at DISPATCH) but published
 * when the worker's result lands (at APPLY) — several frames later, with chunk ingest running in
 * between. During that window the consumer's published state still describes its PREVIOUS mesh, or,
 * for a chunk meshing for the first time, no mesh at all: phase `Loaded`, mask 0. An arrival in that
 * window therefore read "this consumer isn't waiting on anything", declined to re-queue, and the
 * in-flight mesh then landed carrying the extrapolated column. Nothing could ever ask again — the
 * source had already arrived, so no future arrival names it, and the convergence sweep deliberately
 * doesn't re-queue a chunk that draws. The result was a PERMANENT wrong edge or corner column:
 * stationary hairline gaps and pinholes between same-LOD chunks, worst on steep terrain (where the
 * extrapolated clamp is off by a whole voxel of weight) and seeded during exactly the busy streaming
 * that makes the window wide. Pinned by meshOrderIndependence.test.ts.
 *
 * The ledger below makes an in-flight dispatch's extrapolation VISIBLE to the trigger, so the
 * decision reads "what the mesh on screen extrapolated, OR what the mesh on its way extrapolated".
 */

import { ChunkPhase } from './Chunk.js';

/**
 * Does the consumer at NEGATIVE_MARGIN_OFFSETS_7[sourceIndex] need to re-mesh now that the chunk at
 * the mirrored positive offset has arrived?
 *
 * Two markers, and it takes both:
 *  - `MeshedIncomplete` — a high FACE was skipped, so the mesh has a boundary hole and never draws.
 *  - mask bit `sourceIndex` — this exact source was extrapolated. Necessary because a consumer
 *    missing only a DIAGONAL (+XY/+XZ/+YZ/+XYZ) is topologically complete and therefore DRAWN, with a
 *    one-vertex-wide edge/corner column built from the wrong value.
 *
 * Exact, not churn: the two offset tables are index-wise mirrors, so bit `sourceIndex` of the
 * consumer's mask names precisely the chunk that just arrived. A consumer that already had it is
 * left alone.
 */
export function marginConsumerNeedsRemesh(
  phase: ChunkPhase,
  unconvergedMask: number,
  sourceIndex: number,
): boolean {
  return phase === ChunkPhase.MeshedIncomplete || (unconvergedMask & (1 << sourceIndex)) !== 0;
}

/**
 * Which margin sources the meshes currently BETWEEN dispatch and apply had to extrapolate, per chunk.
 *
 * Records the union of every outstanding dispatch's mask, and forgets a chunk only once all of its
 * dispatches have landed — a chunk can legitimately have two outstanding (the second queued after a
 * source arrived, dispatched once the first left the worker), and clearing on the first apply would
 * re-open the window for the second.
 *
 * Failure direction is deliberate: a record that never settles (a dispatch dropped without applying)
 * costs at most one redundant re-mesh if that position ever loads again. Losing a record costs a
 * permanent crack, so the ledger over-remembers rather than under-remembers.
 */
export class MarginFlightLedger {
  /** key → OR of every outstanding dispatch's extrapolated-margin mask. */
  private masks = new Map<string, number>();
  /** key → number of dispatches recorded but not yet settled. */
  private outstanding = new Map<string, number>();

  /** A mesh was dispatched for `key`, expanded while `mask` named the absent sources. */
  record(key: string, mask: number): void {
    this.outstanding.set(key, (this.outstanding.get(key) ?? 0) + 1);
    this.masks.set(key, (this.masks.get(key) ?? 0) | mask);
  }

  /** A dispatch for `key` has applied (its mask is now published on the chunk). */
  settle(key: string): void {
    const n = this.outstanding.get(key);
    if (n === undefined) return;
    if (n > 1) { this.outstanding.set(key, n - 1); return; }
    this.outstanding.delete(key);
    this.masks.delete(key);
  }

  /** Sources extrapolated by meshes still in flight for `key` (0 when nothing is outstanding). */
  maskFor(key: string): number {
    return this.masks.get(key) ?? 0;
  }

  /**
   * THE input to `marginConsumerNeedsRemesh`: every source `key` is still waiting on — those its
   * APPLIED mesh extrapolated (`appliedMask`, i.e. `Chunk.extrapolatedMargins`, describing what is on
   * screen) together with those any mesh of it now in flight extrapolated. One definition, shared by
   * the pipeline and by the order-independence harness, so neither can drift from the other.
   */
  unconvergedFor(key: string, appliedMask: number): number {
    return appliedMask | this.maskFor(key);
  }

  /** Chunks with at least one outstanding dispatch — a gauge, not a decision input. */
  get size(): number {
    return this.outstanding.size;
  }
}
